import React from "react";
import { Badge, Card, Flex, Typography } from "antd";
import { statusLabelMap } from "../../ticket/ticketConstants";
import { WO_STATUS_COLORS } from "../dashboard.constants";
import { palette } from "../../theme";

type Props = {
  countByStatus: Record<string, number>;
  total: number;
  loading?: boolean;
};

const RADIUS = 54;
const STROKE = 18;
const SIZE = 148;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const STATUSES = ["NEW", "IN_PROGRESS", "IN_REVIEW", "STANDBY", "DONE"];

export const WorkOrdersByStatusChart: React.FC<Props> = ({ countByStatus, total, loading }) => {
  let cumulativeDash = 0;
  const slices = STATUSES.map((status) => {
    const count = countByStatus[status] ?? 0;
    const fraction = total > 0 ? count / total : 0;
    const dash = fraction * CIRCUMFERENCE;
    const gap = CIRCUMFERENCE - dash;
    const sliceOffset = cumulativeDash;
    cumulativeDash += dash;
    return { status, count, dash, gap, offset: sliceOffset };
  });

  return (
    <Card title="Work Orders by Status" loading={loading} style={{ height: "100%" }}>
      <Flex gap={24} align="center">
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ flexShrink: 0 }}
        >
          <g transform={`rotate(-90, ${SIZE / 2}, ${SIZE / 2})`}>
            {total === 0 ? (
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke={palette.cardBorder}
                strokeWidth={STROKE}
              />
            ) : (
              slices.map(({ status, dash, gap, offset: sliceOffset }) => (
                <circle
                  key={status}
                  cx={SIZE / 2}
                  cy={SIZE / 2}
                  r={RADIUS}
                  fill="none"
                  stroke={WO_STATUS_COLORS[status] ?? palette.cardBorder}
                  strokeWidth={STROKE}
                  strokeDasharray={`${dash} ${gap}`}
                  strokeDashoffset={-sliceOffset}
                />
              ))
            )}
          </g>
          <text
            x={SIZE / 2}
            y={SIZE / 2 - 8}
            textAnchor="middle"
            fontSize="22"
            fontWeight="700"
            fill={palette.textPrimary}
          >
            {total}
          </text>
          <text
            x={SIZE / 2}
            y={SIZE / 2 + 12}
            textAnchor="middle"
            fontSize="11"
            fill={palette.textSecondary}
          >
            total
          </text>
        </svg>

        <Flex vertical gap={8} style={{ flex: 1 }}>
          {slices.map(({ status, count }) => (
            <Flex key={status} justify="space-between" align="center">
              <Badge
                color={WO_STATUS_COLORS[status]}
                text={
                  <Typography.Text style={{ fontSize: 13 }}>
                    {statusLabelMap[status] ?? status}
                  </Typography.Text>
                }
              />
              <Typography.Text strong style={{ fontSize: 13 }}>
                {count}
              </Typography.Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
    </Card>
  );
};
